import { zodResolver } from '@hookform/resolvers/zod';
import { Flex } from 'antd';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { ConditionView, CUSTOM_OPTION_ID, FormSelect, FormTextInput } from '@/shared/ui';

import { FormConstants, IdConstants } from '../../../../components/Forms/constants';
import { useAddressOptions } from '../lib/useAddressOptions';
import {
  ukranianAddressBlockBaseSchema,
  ukranianAddressBlockSchema,
  ukranianAddressBlockSchemaWithoutAppartments,
} from '../model/schemas';
import { OptionalAddressBlockSchema, UkranianAddressBlockSchema } from '../model/types';

const defaultValues: Record<keyof UkranianAddressBlockSchema, null> = {
  region: null,
  district: null,
  settlement: null,
  street: null,
  custom_street: null,
  house: null,
  building: null,
  apartment: null,
  zip_code: null,
};

interface Props {
  address: UkranianAddressBlockSchema;
  country: OptionalAddressBlockSchema['country'];
  revalidate: boolean;
  includeZipCode?: boolean;
  hideAppartments?: boolean;
  handleAddressChange: (formData: Partial<UkranianAddressBlockSchema>) => void;
  withCustomStreet?: boolean;
}

export const UkranianAddressBlock: React.FC<Props> = ({
  address,
  country,
  includeZipCode = false,
  hideAppartments = false,
  revalidate,
  handleAddressChange,
  withCustomStreet = false,
}) => {
  const { control, watch, getValues, trigger, setValue } = useForm<Partial<UkranianAddressBlockSchema>>({
    values: address,
    // @ts-ignore
    resolver: zodResolver(hideAppartments ? ukranianAddressBlockSchemaWithoutAppartments : ukranianAddressBlockSchema),
  });

  const watchedFormData = watch();
  const { region, district, settlement } = watchedFormData;

  const { optionsRegion, optionsDistrict, optionsSettlement, optionsStreet } = useAddressOptions({
    selectedRegionId: region?.id,
    selectedDistrictId: district?.id,
    selectedSettlementId: typeof settlement === 'object' ? settlement?.id : null,
  });

  const isRegionSelectDisabled = useMemo<boolean>(() => isEmpty(country), [country]);
  const isDistrictSelectDisabled = useMemo<boolean>(
    () => isEmpty(region) || region?.id === IdConstants.KYIV || region?.id === IdConstants.SEVASTOPOL,
    [region],
  );
  const isSettlementSelectDisabled = useMemo<boolean>(
    () => region?.id === IdConstants.KYIV || region?.id === IdConstants.SEVASTOPOL || isEmpty(district),
    [region, district],
  );
  const isStreetSelectDisabled = useMemo<boolean>(() => isEmpty(settlement), [settlement]);

  useEffect(() => {
    revalidate && trigger();
  }, [revalidate]);

  useEffect(() => {
    if (isEqual(address, getValues())) return;

    Object.keys(ukranianAddressBlockBaseSchema.shape).forEach((fieldName) => {
      const typedFieldName = fieldName as keyof UkranianAddressBlockSchema;

      setValue(typedFieldName, address[typedFieldName] ?? defaultValues[typedFieldName]);
    });
  }, [address]);

  const handleChange = <FieldName extends keyof typeof watchedFormData>(
    fieldName: FieldName,
    fieldValue: (typeof watchedFormData)[FieldName],
  ): void => {
    if (fieldName === 'house' || fieldName === 'building' || fieldName === 'apartment' || fieldName === 'zip_code') {
      setValue(fieldName, fieldValue);
      revalidate && trigger();
      handleAddressChange(getValues());
      return;
    }

    switch (fieldName) {
      case 'region':
        setValue('district', defaultValues['district']);

      case 'district':
        setValue('settlement', defaultValues['settlement']);

      case 'settlement':
        setValue('street', defaultValues['street']);

      case 'street':
        setValue('house', defaultValues['house']);
        setValue('building', defaultValues['building']);
        setValue('apartment', defaultValues['apartment']);
        setValue('zip_code', defaultValues['zip_code']);

      default:
        if (!fieldValue) break;

        if (typeof fieldValue === 'object') {
          if (fieldName === 'region') {
            if (fieldValue.id === IdConstants.KYIV || fieldValue.id === IdConstants.SEVASTOPOL) {
              setValue('settlement', fieldValue);
            }
          }
          if (fieldName === 'street') {
            if (includeZipCode && 'postCode' in fieldValue && fieldValue.postCode) {
              setValue('zip_code', fieldValue.postCode);
            }
          }
        }

        revalidate && trigger();
        handleAddressChange(getValues());
        break;
    }
  };

  return (
    <>
      <FormSelect
        name="region"
        control={control}
        label="Область"
        options={optionsRegion}
        disabled={isRegionSelectDisabled}
        // @ts-ignore
        onChange={(_, option) => handleChange('region', option)}
      />

      <FormSelect
        name="district"
        control={control}
        label="Район"
        options={optionsDistrict}
        disabled={isDistrictSelectDisabled}
        // @ts-ignore
        onChange={(_, option) => handleChange('district', option)}
      />

      <FormSelect
        name="settlement"
        control={control}
        label="Населений пункт"
        options={optionsSettlement}
        disabled={isSettlementSelectDisabled}
        // @ts-ignore
        onChange={(_, option) => handleChange('settlement', option)}
      />
      <FormSelect
        allowClear={withCustomStreet}
        name="street"
        control={control}
        label="Вулиця"
        options={optionsStreet}
        disabled={isStreetSelectDisabled}
        pinnedOptionLabel={withCustomStreet ? 'Моєї вулиці немає в списку' : undefined}
        // @ts-ignore
        onChange={(_, option) => {
          handleChange(
            'street',
            option.id === CUSTOM_OPTION_ID ? { id: option.id, label: 'Довільне введення' } : option,
          );
          handleChange('custom_street', null);
        }}
      />
      {watchedFormData.street?.id === CUSTOM_OPTION_ID ? (
        <FormTextInput
          name="custom_street"
          maxLength={FormConstants.INPUT_MAX_LENGTH_100}
          control={control}
          label="Напишіть назву своєї вулиці"
          placeholder="Напр. провулок Шевченка"
          onChange={({ target: { value } }) => handleChange('custom_street', value)}
        />
      ) : (
        <></>
      )}

      <ConditionView if={!hideAppartments}>
        <Flex gap={16}>
          <FormTextInput
            name="house"
            control={control}
            label="Номер будинку"
            maxLength={FormConstants.INPUT_MAX_LENGTH_7}
            onChange={({ target: { value } }) => handleChange('house', value)}
          />
          <FormTextInput
            name="building"
            control={control}
            label="Номер корпусу"
            maxLength={FormConstants.INPUT_MAX_LENGTH_5}
            onChange={({ target: { value } }) => handleChange('building', value)}
          />
          <FormTextInput
            name="apartment"
            control={control}
            label="Номер квартири"
            maxLength={FormConstants.INPUT_MAX_LENGTH_5}
            onChange={({ target: { value } }) => handleChange('apartment', value)}
          />
        </Flex>
      </ConditionView>

      <ConditionView if={includeZipCode}>
        <FormTextInput
          name="zip_code"
          control={control}
          label="Індекс"
          maxLength={FormConstants.INPUT_MAX_LENGTH_50}
          onChange={({ target: { value } }) => handleChange('zip_code', value)}
        />
      </ConditionView>
    </>
  );
};
